import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PreferencesStore } from './core/nativeStore';
import './styles.css';
import { createRenderer } from './core/Renderer';
import { Loop } from './core/Loop';
import { Gyro } from './core/gyro';
import { LocalStorageStore, SaveStore } from './core/storage';
import { loadManifest, loadThemes } from './content/manifest';
import { createGlowTexture } from './fx/glow';
import { Hud } from './ui/hud';
import { MapUi } from './ui/mapUi';
import { Onboarding } from './ui/onboarding';
import { Pages } from './ui/pages';
import { confirmOpen } from './ui/placeCard';
import { App, type Page } from './app';
import { installTestHooks } from './testHooks';

function intParam(params: URLSearchParams, key: string, min: number, max: number): number | null {
  const v = Number.parseInt(params.get(key) ?? '', 10);
  return Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : null;
}

/** env(safe-area-inset-*) 的实际像素（CSS 变量 --safe-top / --safe-bottom，测试可注入） */
function safeInsets(): { safeTop: number; safeBottom: number } {
  const probe = document.querySelector<HTMLElement>('#safe-probe')!;
  const cs = getComputedStyle(probe);
  return { safeTop: parseFloat(cs.paddingTop) || 0, safeBottom: parseFloat(cs.paddingBottom) || 0 };
}

async function main(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const seed = intParam(params, 'seed', 1, 2 ** 31 - 1);

  const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas');
  if (!canvas) throw new Error('Missing #game-canvas');
  let renderer;
  try {
    renderer = createRenderer(canvas);
  } catch (err) {
    console.error(err);
    showFatal('这台设备不支持 WebGL，无法显示小镇。');
    return;
  }

  // v2：三座城市，各自一份 manifest（public/content/themes.json 索引）
  const themes = await loadThemes();
  const manifests = await Promise.all(themes.map((t) => loadManifest(t.manifest)));
  manifests.forEach((m, i) => {
    if (m.theme.id !== themes[i].id) throw new Error(`content: ${themes[i].manifest} has theme.id ${m.theme.id}, expected ${themes[i].id}`);
  });
  // App 内（iOS / Android）存档走 Capacitor Preferences，网页走 localStorage
  const prefix = 'paper-town:';
  const store = new SaveStore(Capacitor.isNativePlatform() ? new PreferencesStore(prefix) : new LocalStorageStore(prefix));
  if (Capacitor.isNativePlatform()) void setupNativeChrome();
  const { notice } = await store.load(manifests);

  const gyro = new Gyro();
  gyro.asked = store.save.gyro.asked;
  const hud = new Hud();
  const mapUi = new MapUi();
  // 返回守卫：离开首页时在浏览器历史里压一条，浏览器后退 / Android 返回键都回到 app.back()（同一套导航）
  const nav = { armed: false, ignoreNext: false };
  const syncHistory = (p: Page) => {
    if (p !== 'home' && !nav.armed) {
      history.pushState({ paperTown: 'guard' }, '');
      nav.armed = true;
    } else if (p === 'home' && nav.armed) {
      nav.armed = false;
      nav.ignoreNext = true;
      history.back();
    }
  };
  let app: App;
  const pages = new Pages({
    continue: () => void app.continueGame(),
    journey: () => app.showCities(),
    tarot: () => void app.openTarot(),
    daily: () => app.showDaily(),
    album: () => app.showAlbum(),
    settings: () => app.showSettings(),
    openCity: (id) => void app.openCity(id),
    lockedCity: (id) => void app.openCity(id),
    playDaily: () => void app.playDaily(),
    albumTab: (id) => app.showAlbum(id),
    albumOpen: (themeId, placeId) => void app.enterView(themeId, placeId, 'album'),
    setHaptics: (on) => app.setHaptics(on),
    setGyro: (on) => app.setGyroSetting(on),
    resetSave: () => void app.resetSave(),
  });
  app = new App({
    renderer, themes, manifests, store, gyro, hud, mapUi, pages, onboarding: new Onboarding(), glowTexture: createGlowTexture(), seed,
    version: __APP_VERSION__,
    onPage: (p) => syncHistory(p),
  });
  history.replaceState({ paperTown: 'root' }, '');
  window.addEventListener('popstate', () => {
    if (nav.ignoreNext) {
      nav.ignoreNext = false;
      return;
    }
    nav.armed = false;
    void app.back().finally(() => syncHistory(app.page));
  });
  /** Android 返回键：首页（且没有确认框）退出 App，其余回上一级 */
  const hardwareBack = (): 'exit' | 'back' => {
    if (app.page === 'home' && !confirmOpen()) {
      if (Capacitor.isNativePlatform()) void NativeApp.exitApp();
      return 'exit';
    }
    void app.back().finally(() => syncHistory(app.page));
    return 'back';
  };
  if (Capacitor.isNativePlatform()) void NativeApp.addListener('backButton', () => hardwareBack());

  const hourParam = params.get('hour');
  if (hourParam !== null && Number.isFinite(Number(hourParam))) app.hourOverride = Number(hourParam);

  // 手机浏览器的地址栏 / 工具栏伸缩、横竖屏切换时，画布的 CSS 尺寸会变，但 window 'resize' 不一定准时到
  // （iOS Safari、微信内置浏览器常见：事件先到、布局后变，或者干脆不发）。尺寸没跟上时画面被拉伸，
  // 棋盘压到底部按钮上，手指和格子也对不上。所以除了 resize 事件，还用 ResizeObserver 和每帧比对兜底。
  let lastW = 0;
  let lastH = 0;
  let lastDpr = 0;
  const resize = () => {
    const vp = { width: canvas.clientWidth, height: canvas.clientHeight, ...safeInsets() };
    if (vp.width === 0 || vp.height === 0) return;
    lastW = vp.width;
    lastH = vp.height;
    lastDpr = window.devicePixelRatio || 1;
    renderer.setPixelRatio(Math.min(lastDpr, 2));
    renderer.setSize(vp.width, vp.height, false);
    app.setViewport(vp);
  };
  const resizeIfChanged = () => {
    if (canvas.clientWidth !== lastW || canvas.clientHeight !== lastH || (window.devicePixelRatio || 1) !== lastDpr) resize();
  };
  window.addEventListener('resize', resize);
  window.addEventListener('orientationchange', () => setTimeout(resize, 300));
  window.visualViewport?.addEventListener('resize', resizeIfChanged);
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resizeIfChanged).observe(canvas);
  resize();

  // 开发入口：?page=play[&theme=id][&place=id][&n=][&seed=] 直接进拼图（跳过首页、地图、地点卡和新手引导）
  if (params.get('page') === 'play') {
    const themeId = params.get('theme') ?? app.themeIds[0];
    app.themeId = themeId;
    const placeId = params.get('place') ?? app.places[0].id;
    const n = intParam(params, 'n', 3, 6) ?? 4;
    await app.enterPlay({ mode: 'journey', themeId, placeId, n }, null, { onboarding: false });
  } else {
    app.showHome();
  }
  if (notice) mapUi.toast(notice);

  const local = (e: PointerEvent | MouseEvent) => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  canvas.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    canvas.setPointerCapture(e.pointerId);
    const p = local(e);
    app.pointerDown(p.x, p.y, e.pointerId);
  });
  canvas.addEventListener('pointermove', (e) => {
    const p = local(e);
    app.pointerMove(p.x, p.y, e.pointerId);
  });
  canvas.addEventListener('pointerup', (e) => {
    const p = local(e);
    app.pointerUp(p.x, p.y, e.pointerId);
  });
  // iOS Safari 无视 user-scalable=no：双指缩放 / 双击放大会把整页放大，之后坐标全乱。拼图页再禁掉整页拖动
  // （否则手指从按钮区滑过会带动页面回弹、收起地址栏）。其它页面的列表仍可正常滚动。
  for (const ev of ['gesturestart', 'gesturechange', 'gestureend']) document.addEventListener(ev, (e) => e.preventDefault());
  document.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length > 1 || app.page === 'play' || app.page === 'popup') e.preventDefault();
    },
    { passive: false },
  );
  canvas.addEventListener('pointercancel', (e) => app.pointerCancel(e.pointerId));
  // 丢失捕获且没有收到 up/cancel 时也要清理手势，否则之后所有输入失效（pointerUp 之后再触发是无害的空操作）
  canvas.addEventListener('lostpointercapture', (e) => app.pointerCancel(e.pointerId));
  document.addEventListener('visibilitychange', () => app.setHidden(document.hidden));

  let frame = 0;
  let loopPaused = false;
  window.__THREE_GAME_DIAGNOSTICS__ = { frame };
  new Loop(
    (dt) => {
      frame += 1;
      if (!loopPaused) app.update(dt * 1000);
      window.__THREE_GAME_DIAGNOSTICS__ = { frame };
    },
    () => {
      resizeIfChanged();
      app.render();
    },
  ).start();
  installTestHooks({ app, store, setLoopPaused: (p) => (loopPaused = p), resize, hardwareBack });
  if (import.meta.env.VITE_AUTOPILOT === '1') void import('./devAutopilot').then((m) => m.runAutopilot(app));
}

function showFatal(text: string): void {
  const box = document.createElement('div');
  box.className = 'fatal';
  box.textContent = text;
  (document.querySelector('#app') ?? document.body).appendChild(box);
}

main().catch((err) => {
  console.error(err);
  showFatal('加载失败，请刷新重试');
});

/** 状态栏：浅色纸面背景上用深色字；WebView 铺满到状态栏下，靠 env(safe-area-inset-*) 让开 */
async function setupNativeChrome(): Promise<void> {
  try {
    await StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  } catch (err) {
    console.warn('status bar setup failed', err);
  }
}
